import { Brand } from 'designs';

/**
 * Every URL the site answers on.
 *
 * The legal documents sit at the root, not under `/aquarist`: they cover both
 * apps and are reachable from the footer of every page, so they belong to the
 * site rather than to one brand.
 */
export const ROUTES = {
  home: '/',
  aquarist: '/aquarist',
  idleCafe: '/idlecafe',
  privacy: '/privacypolicy',
  terms: '/termsandconditions',
  gdpr: '/gdprcompliance',
} as const;

export type Route = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * The legal documents. One list: the footer renders it, and `LEGAL_ROUTES`
 * below — the check `brandForPath` makes first — is derived from it, so a
 * document cannot be linked without also being routed to the right brand.
 */
export const LEGAL_LINKS = [
  { to: ROUTES.privacy, label: 'Privacy Policy' },
  { to: ROUTES.gdpr, label: 'GDPR Policy' },
  { to: ROUTES.terms, label: 'Terms and Conditions' },
] as const;

const LEGAL_ROUTES: readonly string[] = LEGAL_LINKS.map((link) => link.to);

/**
 * The addresses these documents used to live at. The app store listings point
 * at them, so they must keep working: each one redirects to its replacement
 * rather than 404ing.
 */
export const LEGACY_LEGAL_ROUTES = [
  { from: '/aquarist/privacypolicy', to: ROUTES.privacy },
  { from: '/aquarist/termsandconditions', to: ROUTES.terms },
  { from: '/aquarist/gdprcompliance', to: ROUTES.gdpr },
] as const;

const LEGACY_LEGAL_PATHS: readonly string[] = LEGACY_LEGAL_ROUTES.map(
  (route) => route.from
);

/**
 * Which brand's identity a given path is dressed in.
 *
 * The legacy paths are checked alongside the current ones because they sit
 * under `/aquarist`, and without that they would be dressed as Aquarist for
 * the single render before the redirect fires.
 */
export const brandForPath = (pathname: string): Brand => {
  if (LEGAL_ROUTES.includes(pathname)) return 'midnightswim';
  if (LEGACY_LEGAL_PATHS.includes(pathname)) return 'midnightswim';
  if (pathname.startsWith(ROUTES.aquarist)) return 'aquarist';
  if (pathname.startsWith(ROUTES.idleCafe)) return 'idlecafe';
  return 'midnightswim';
};
